import { Injectable } from "@angular/core";
import { Action, Select, Selector, State, StateContext } from "@ngxs/store";
import { catchError, Observable, of, switchMap, tap } from "rxjs";

import { Wallet } from "../model/wallet.model";

import { ClearWallet, GetWallets, ReloadWallets } from "./wallet.action";
import {
  GetDonateWalletTransactions,
  GetStoreWalletTransactions} from "./transaction.action";
import { WalletService } from "../service/wallet.service";
import { TransactionService } from "../service/transaction.service";
import { fromTransactionResponseToTransaction } from "../mapper/transaction.mapper";
import { fromWalletResponseToWallet } from "../mapper/wallet.mapper";
import { AccountState } from "../../account/state/account.state";
import { TransactionData } from "../model/transaction.model";

export class WalletStateModel {
  wallets: Wallet[];
  walletError: Error;
  walletIsLoading: boolean;
  storeTransactionsError: Error;
  storeTransactionsIsLoading: boolean;
  donateTransactionsError: Error;
  donateTransactionsIsLoading: boolean;
}

@State<WalletStateModel>({
  name: "wallet",
  defaults: {
    wallets: undefined!,
    walletError: undefined!,
    walletIsLoading: true,
    storeTransactionsError: undefined!,
    storeTransactionsIsLoading: true,
    donateTransactionsError: undefined!,
    donateTransactionsIsLoading: true}})
@Injectable()
export class WalletState {
  @Select(AccountState.accountId) accountId$: Observable<string>;

  constructor(
    private _walletService: WalletService,
    private _transactionService: TransactionService,
  ) {}

  @Selector()
  static wallets(state: WalletStateModel) {
    return state.wallets;
  }

  @Selector()
  static donateWallet(state: WalletStateModel) {
    return state.wallets?.find((wallet) => wallet.type === "DONATE");
  }

  @Selector()
  static storeWallet(state: WalletStateModel) {
    return state.wallets?.find((wallet) => wallet.type === "MARKET");
  }

  @Selector()
  static donateTransactionPage(state: WalletStateModel): {
    [page: number]: TransactionData[];
  } {
    return (
      state.wallets?.find((wallet: Wallet) => wallet.type === "DONATE")
        ?.transactions?.data ?? []
    );
  }

  @Selector()
  static donateTransactionPagination(state: WalletStateModel) {
    return state.wallets?.find((wallet) => wallet.type === "DONATE")
      ?.transactions?.pagination;
  }

  @Selector()
  static storeTransactionPage(state: WalletStateModel): {
    [page: number]: TransactionData[];
  } {
    return (
      state.wallets?.find((wallet: Wallet) => wallet.type === "MARKET")
        ?.transactions?.data ?? []
    );
  }

  @Selector()
  static storeTransactionPagination(state: WalletStateModel) {
    return state.wallets?.find((wallet) => wallet.type === "MARKET")
      ?.transactions?.pagination;
  }

  @Selector()
  static walletIsLoading(state: WalletStateModel): boolean {
    return state.walletIsLoading;
  }

  @Selector()
  static walletHasError(state: WalletStateModel): boolean {
    return !!state.walletError;
  }

  @Selector()
  static storeTransactionsIsLoading(state: WalletStateModel): boolean {
    return state.storeTransactionsIsLoading;
  }

  @Selector()
  static storeTransactionsHasError(state: WalletStateModel): boolean {
    return !!state.storeTransactionsError;
  }

  @Selector()
  static donateTransactionsIsLoading(state: WalletStateModel): boolean {
    return state.donateTransactionsIsLoading;
  }

  @Selector()
  static donateTransactionsHasError(state: WalletStateModel): boolean {
    return !!state.donateTransactionsError;
  }

  @Action(GetWallets)
  getWallets(ctx: StateContext<WalletStateModel>) {
    const state = ctx.getState();
    if (state.wallets && state.wallets.length > 0) {
      return of(state.wallets);
    } else {
      ctx.patchState({
        walletIsLoading: true});

      return this._walletService.getWallets().pipe(
        tap({
          next: (response) => {
            const wallets = response.map((wallet) => {
              return fromWalletResponseToWallet(wallet);
            });
            ctx.patchState({
              wallets,
              walletError: undefined!,
              walletIsLoading: false});
          },
          error: (error) => {
            ctx.patchState({
              walletError: error,
              walletIsLoading: false});
            return of();
          }}),
      );
    }
  }

  @Action(GetStoreWalletTransactions)
  getStoreWalletTransactions(
    ctx: StateContext<WalletStateModel>,
    action: GetStoreWalletTransactions,
  ) {
    const state = ctx.getState();

    if (state.wallets === undefined) {
      return of(null);
    }

    const marketWalletIndex = state.wallets.findIndex(
      (wallet) => wallet.type === "MARKET",
    );

    if (marketWalletIndex === -1) {
      return of(null);
    }

    const marketWallet = state.wallets[marketWalletIndex];

    const alreadyLoaded =
      marketWallet.transactions &&
      marketWallet.transactions.data &&
      marketWallet.transactions.data[action.page];

    if (alreadyLoaded) {
      return of(marketWallet.transactions);
    }

    ctx.patchState({
      storeTransactionsIsLoading: true});

    return this._transactionService
      .getTransactionsByWalletId(
        marketWallet.id,
        action.page,
        action.pageSize,
        "ORDER_COMMISSION",
      )
      .pipe(
        tap((response) => {
          const newTransactions =
            fromTransactionResponseToTransaction(response);

          const currentData =
            marketWallet.transactions &&
            typeof marketWallet.transactions.data === "object" &&
            !Array.isArray(marketWallet.transactions.data)
              ? marketWallet.transactions.data
              : {};

          const mergedTransactions = {
            ...marketWallet.transactions,
            data: {
              ...currentData,
              [action.page]: newTransactions.data[action.page]},
            pagination: newTransactions.pagination};

          const wallets = [...state.wallets];
          wallets[marketWalletIndex] = {
            ...marketWallet,
            transactions: mergedTransactions};

          ctx.patchState({
            wallets,
            storeTransactionsIsLoading: false,
            storeTransactionsError: undefined!});
        }),
        catchError((error) => {
          ctx.patchState({
            storeTransactionsError: error,
            storeTransactionsIsLoading: false});
          return of();
        }),
      );
  }

  @Action(GetDonateWalletTransactions)
  getDonateWalletTransactions(
    ctx: StateContext<WalletStateModel>,
    action: GetDonateWalletTransactions,
  ) {
    const state = ctx.getState();

    if (state.wallets === undefined) {
      return of(null);
    }

    const donateWalletIndex = state.wallets.findIndex(
      (wallet) => wallet.type === "DONATE",
    );

    if (donateWalletIndex === -1) {
      return of(null);
    }

    const donateWallet = state.wallets[donateWalletIndex];

    const alreadyLoaded =
      donateWallet.transactions &&
      donateWallet.transactions.data &&
      donateWallet.transactions.data[action.page];

    if (alreadyLoaded) {
      return of(donateWallet.transactions);
    }

    ctx.patchState({
      donateTransactionsIsLoading: true});

    return this._transactionService
      .getTransactionsByWalletId(
        donateWallet.id,
        action.page,
        action.pageSize,
        "DONATION",
      )
      .pipe(
        tap((response) => {
          const newTransactions =
            fromTransactionResponseToTransaction(response);

          const currentData =
            donateWallet.transactions &&
            typeof donateWallet.transactions.data === "object" &&
            !Array.isArray(donateWallet.transactions.data)
              ? donateWallet.transactions.data
              : {};

          const mergedTransactions = {
            ...donateWallet.transactions,
            data: {
              ...currentData,
              [action.page]: newTransactions.data[action.page]},
            pagination: newTransactions.pagination};

          const wallets = [...state.wallets];
          wallets[donateWalletIndex] = {
            ...donateWallet,
            transactions: mergedTransactions};

          ctx.patchState({
            wallets,
            donateTransactionsError: undefined!,
            donateTransactionsIsLoading: false});
        }),
        catchError((error) => {
          ctx.patchState({
            donateTransactionsError: error,
            donateTransactionsIsLoading: false});
          return of();
        }),
      );
  }

  @Action(ReloadWallets)
  reloadWallets(ctx: StateContext<WalletStateModel>, action: ReloadWallets) {
    ctx.patchState({
      wallets: undefined!,
      walletError: undefined!,
      walletIsLoading: true,
      storeTransactionsError: undefined!,
      storeTransactionsIsLoading: true,
      donateTransactionsError: undefined!,
      donateTransactionsIsLoading: true});

    ctx.dispatch(new GetWallets());
    ctx.dispatch(new GetStoreWalletTransactions(action.page, action.pageSize));
    ctx.dispatch(new GetDonateWalletTransactions(action.page, action.pageSize));

    return of();
  }

  @Action(ClearWallet)
  clearWallet(ctx: StateContext<WalletStateModel>) {
    ctx.patchState({
      wallets: undefined!,
      walletError: undefined!,
      walletIsLoading: true,
      storeTransactionsError: undefined!,
      storeTransactionsIsLoading: true,
      donateTransactionsError: undefined!,
      donateTransactionsIsLoading: true});
    return of();
  }
}
